'use client'

import { Button, ButtonProps } from '@nextui-org/button'
import { extendVariants } from '@nextui-org/system'

import { clsx } from 'clsx'

import { FC, forwardRef, LegacyRef } from 'react'

import { IconCircleArrow } from '@/app/shared/icons'
import { IChildren, IClassName } from '@/app/shared/interfaces/common.interface'
import { bthHover } from '@/app/shared/ui/button/constants'
import { generateSVgHover } from '@/app/shared/ui/button/services'

// elements
export const CustomButtonComponent = extendVariants(Button, {
  variants: {
    variant: {
      bordered: '',
      solid: '',
      light: 'data-[hover=true]:bg-content1 data-[present-true]:bg-content1',
      flat: '',
    },
    size: {
      lg: 'h-[60px] w-full',
      md: 'h-[50px] w-full',
    },
    color: {
      default: `${bthHover} data-[hover=true]:border-primary data-[present-true]:border-primary bg-content1-200`,
      primary: `${bthHover} data-[hover=true]:border-primary data-[present-true]:border-primary`,
      success: `${bthHover} data-[hover=true]:border-success data-[present-true]:border-success text-content1-700`,
      secondary: `${bthHover} data-[hover=true]:border-secondary data-[present-true]:border-secondary`,
      danger: `${bthHover} data-[hover=true]:border-danger data-[present-true]:border-danger`,
      warning: `${bthHover} data-[hover=true]:border-warning data-[present-true]:border-warning`,
    },
  },
})

// interface
interface IButton extends IChildren, IClassName, Omit<ButtonProps, 'ref' | 'onClick'> {
  icon?: string | boolean
  ref?: LegacyRef<any>
  onClick?: () => void
  customColor?: string
  iconColor?: string
}

const ButtonComponent: FC<Readonly<IButton>> = forwardRef(
  ({ children, className, icon, onClick, isLoading, customColor, iconColor, ...props }, ref) => {
    const handleClick = () => {
      if (!onClick) return

      onClick()
    }

    return (
      <CustomButtonComponent
        {...props}
        onPress={handleClick}
        ref={ref}
        disableRipple={props.variant === 'light'}
        className={clsx(
          'group relative flex min-h-10 min-w-0 gap-0 overflow-hidden whitespace-pre-wrap rounded-[15px] px-[30px] font-semibold uppercase mobile:min-w-2 tablet:min-w-[63px]',
          className,
          {
            '[&>div]:-translate-x-2.5': isLoading,
          },
        )}
        isLoading={isLoading}
      >
        {children}
        {!!icon && (
          <IconCircleArrow
            className={clsx(
              'ml-5 h-[32px] w-[32px] flex-shrink-0 transition-colors duration-300',
              generateSVgHover(props.color),
              icon,
            )}
            style={{
              color: customColor,
              '--custom-fill-color': iconColor ?? '#000000',
            }}
          />
        )}
      </CustomButtonComponent>
    )
  },
)

ButtonComponent.displayName = 'ButtonComponent'

export default ButtonComponent
